import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Currency,
  NegotiationRoundStatus,
  SourcingEventV2Dto,
  useAwardSourcingEventMutation,
  useGetEvaluationQuery,
  type EvalBidDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { Explanation } from "../../../../common/support/Explanation/Explanation";
import { useFinalStageRound, useRoundSelection } from "../rounds/RoundSelectionContext";
import { AwardBidsTable } from "./AwardBidsTable";
import { AwardDecisionHistory } from "./AwardDecisionHistory";
import {
  failsMinimumRequirement,
  hasUnassessedMinimumRequirements,
} from "../minimum-requirements/MinimumRequirementOutcomeBadge";
import { seedWinnerIds } from "./seedWinnerIds";
import { SupplierCountIndicator } from "./SupplierCountIndicator";

export const SelectWinner = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiError();
  const finalStageRound = useFinalStageRound();
  const { rounds } = useRoundSelection();
  // ADR-033 §5: awarding is blocked while any negotiation round is still open.
  const hasOpenRound = rounds.some((round) => round.status !== NegotiationRoundStatus.Completed);
  const { data: evaluation, isLoading, isError } = useGetEvaluationQuery({
    eventId: event.id,
    round: finalStageRound?.id,
  });
  const [award, { isLoading: isAwarding }] = useAwardSourcingEventMutation();

  const [winnerIds, setWinnerIds] = useState<string[]>([]);
  const [internalNote, setInternalNote] = useState("");
  const selectWinnerPublicCtx = event.eventContext.type === "Public" ? event.eventContext : null;
  const [currency, setCurrency] = useState<Currency>(selectWinnerPublicCtx?.estimatedValue?.currency ?? Currency.Nok);
  const [amount, setAmount] = useState(
    selectWinnerPublicCtx?.estimatedValue?.value != null ? String(selectWinnerPublicCtx.estimatedValue.value) : "",
  );

  // ADR-033 D6: shopped products pre-select their suppliers' bids (multi-winner).
  const stageBasket = (finalStageRound ? finalStageRound.selectedProductBids : event.selectedProductBids) ?? [];
  const basketBidIds = [
    ...new Set(stageBasket.flatMap((group) => group.bids.flatMap((product) => product.bids))),
  ].join(",");

  useEffect(() => {
    if (!evaluation) return;
    const validIds = evaluation.bids.map((b) => b.bidId);
    setWinnerIds((prev) => {
      const stillValid = prev.filter((id) => validIds.includes(id));
      if (stillValid.length > 0) return stillValid.length === prev.length ? prev : stillValid;
      return seedWinnerIds(evaluation, basketBidIds.split(",").filter(Boolean));
    });
  }, [evaluation, basketBidIds]);

  const maxParticipants = selectWinnerPublicCtx?.frameworkMaxParticipants ?? undefined;

  const toggleWinner = (bidId: string) =>
    setWinnerIds((prev) => (prev.includes(bidId) ? prev.filter((id) => id !== bidId) : [...prev, bidId]));

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (isError || !evaluation) {
    return (
      <Center py="16">
        <Text color="smError">{t("Could not load the evaluation.")}</Text>
      </Center>
    );
  }
  if (evaluation.bids.length === 0) {
    return (
      <Center py="16">
        <Text color="smMuted">{t("No delivered bids to award yet.")}</Text>
      </Center>
    );
  }

  const submit = async () => {
    if (winnerIds.length === 0) return;
    const parsed = amount.trim() === "" ? null : Number(amount);
    try {
      await award({
        eventId: event.id,
        awardSourcingEventRequest: {
          winningBidIds: winnerIds,
          internalNote: internalNote.trim() || null,
          eventValue: parsed != null && !Number.isNaN(parsed) ? { value: parsed, currency } : null,
        },
      }).unwrap();
      toast({ title: t("Event awarded"), status: "success", position: "top-right" });
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Stack
      spacing="6"
      bg="smBackground"
      rounded="xl"
      border="1px solid"
      borderColor="smBorder"
      p={{ base: "4", md: "6" }}
    >
      <AwardDecisionHistory event={event} />

      <Stack spacing="2">
        <AwardBidsTable evaluation={evaluation} mode="select" selectedBidIds={winnerIds} onToggle={toggleWinner} />
        <SupplierCountIndicator selectedCount={winnerIds.length} maxParticipants={maxParticipants} />
      </Stack>

      <MinimumRequirementWarning
        notFulfilled={supplierNames(evaluation.bids, winnerIds, failsMinimumRequirement)}
        unassessed={supplierNames(evaluation.bids, winnerIds, hasUnassessedMinimumRequirements)}
      />

      <FormControl>
        <FormLabel>{t("Estimated contract value")}</FormLabel>
        <HStack>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            bg="smBackground"
            maxW="xs"
            placeholder="0"
          />
          <Select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} bg="smBackground" maxW="32">
            {Object.values(Currency).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </HStack>
      </FormControl>

      <FormControl>
        <FormLabel>{t("Internal note")}</FormLabel>
        <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} bg="smBackground" rows={2} />
        <FormHelperText>{t("Only visible to your organization.")}</FormHelperText>
      </FormControl>

      <Flex justify="end" align="center" gap="3">
        <Text fontSize="sm" color="smMuted">
          {t("You'll prepare and send award letters to each supplier after awarding.")}
        </Text>
        <Explanation text={t("Complete all negotiation rounds before awarding")} enabled={hasOpenRound}>
          <Button
            colorScheme="teal"
            onClick={submit}
            isLoading={isAwarding}
            isDisabled={winnerIds.length === 0 || hasOpenRound}
          >
            {t("Award to selected suppliers")}
          </Button>
        </Explanation>
      </Flex>
    </Stack>
  );
};

const supplierNames = (
  bids: EvalBidDto[],
  winnerIds: string[],
  matches: (bid: EvalBidDto) => boolean,
): string[] =>
  bids.filter((bid) => winnerIds.includes(bid.bidId) && matches(bid)).map((bid) => bid.supplierName);

const MinimumRequirementWarning = ({
  notFulfilled,
  unassessed,
}: {
  notFulfilled: string[];
  unassessed: string[];
}) => {
  const { t } = useTranslation();
  if (notFulfilled.length === 0 && unassessed.length === 0) return null;
  return (
    <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start">
      <AlertIcon />
      <Stack spacing="2">
        {notFulfilled.length > 0 && (
          <Box>
            <AlertTitle>
              {t("Minimum requirements are not fulfilled: {{suppliers}}", {
                suppliers: notFulfilled.join(", "),
              })}
            </AlertTitle>
            <AlertDescription>
              {t(
                "A tender that does not meet an absolute minimum requirement must be rejected, not awarded. Review the minimum requirements before you award.",
              )}
            </AlertDescription>
          </Box>
        )}
        {unassessed.length > 0 && (
          <Box>
            <AlertTitle>
              {t("Minimum requirements are not fully assessed: {{suppliers}}", {
                suppliers: unassessed.join(", "),
              })}
            </AlertTitle>
            <AlertDescription>
              {t(
                "You do not yet know whether the tender meets the minimum requirements. Assess them before you award.",
              )}
            </AlertDescription>
          </Box>
        )}
      </Stack>
    </Alert>
  );
};
